import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Col,
  Row,
  Spin,
  Form,
  Button,
  DatePicker,
  Typography,
  Modal,
  message,
} from 'antd';
import { SearchOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import { getRecibos } from 'api/recaudacion/recibos';
import { RFC_PATTERN, CURP_PATTERN, digitoVerificador } from 'utils/patterns';
import getFormattedValues from 'utils/formatValues';
import NumericInput from 'components/NumericInput';
import { onError } from 'utils/handlers';
import { getModelPermission } from 'utils/permission/index';

const { Text } = Typography;

const BusquedaRecibo = ({
  onFinishBusqueda,
  visible,
  setVisible,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [permissionCiudadano, setPermissionCiudadano] = useState(null);

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _permissionCiudadano = getModelPermission('ciudadano');
      if (mounted) {
        setPermissionCiudadano(_permissionCiudadano);
      }
    };
    validatePermission();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const params = getFormattedValues(values);
      const keys = Object.keys(params);
      const validProps = keys.filter((prop) => params[prop]);
      if (validProps.length) {
        const _recibos = await getRecibos({ ...params, page: 1 });
        if (_recibos) {
          onFinishBusqueda(_recibos, params);
        } else {
          message.info('No se encontraron coincidencias');
        }
        onCancel();
      } else {
        message.info('Ingrese al menos un parámetro de búsqueda');
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rules = {
    cuenta_unica__RFC: [
      {
        pattern: RFC_PATTERN,
        message: 'Introduzca un RFC válido',
      },
    ],
    cuenta_unica__CURP: [
      {
        validator: async (r, value) => {
          if (value) {
            const validado = value.toUpperCase().match(CURP_PATTERN);
            if (!validado || validado[2] !== digitoVerificador(validado[1])) {
              throw new Error('Ingrese una CURP válida');
            }
          }
        },
      },
    ],
    cuenta_unica__email: [
      {
        type: 'email',
        message: 'Introduzca un correo electrónico válido',
      },
    ],
    cuenta_unica__nombre: [
      {
        validator: async (r, value) => {
          const {
            cuenta_unica__apellido_paterno,
            cuenta_unica__apellido_materno,
          } = form.getFieldsValue();
          if (!value && ((cuenta_unica__apellido_paterno && !cuenta_unica__apellido_materno)
            || (!cuenta_unica__apellido_paterno && cuenta_unica__apellido_materno))) {
            throw new Error('Ingrese al menos uno de estos campos');
          }
        },
      },
    ],
    cuenta_unica__apellido_paterno: [
      {
        validator: async (r, value) => {
          const {
            cuenta_unica__nombre,
            cuenta_unica__apellido_materno,
          } = form.getFieldsValue();
          if (!value && ((cuenta_unica__nombre && !cuenta_unica__apellido_materno)
            || (!cuenta_unica__nombre && cuenta_unica__apellido_materno))) {
            throw new Error('Ingrese al menos uno de estos campos');
          }
        },
      },
    ],
    cuenta_unica__apellido_materno: [
      {
        validator: async (rule, value) => {
          const {
            cuenta_unica__nombre,
            cuenta_unica__apellido_paterno,
          } = form.getFieldsValue();
          if (!value && ((cuenta_unica__nombre && !cuenta_unica__apellido_paterno)
            || (!cuenta_unica__nombre && cuenta_unica__apellido_paterno))) {
            throw new Error('Ingrese al menos uno de estos campos');
          }
        },
      },
    ],
    cuenta_unica__numero_de_celular: [
      {
        pattern: /^[0-9]{10}$/,
        message: 'Ingrese un número telefónico válido',
      },
    ],

  };

  const Title = ({ text }) => (
    <Col span={24}>
      <Text style={{ color: '#000000DA', fontSize: 17 }}>
        {text}
      </Text>
    </Col>
  );

  Title.propTypes = { text: PropTypes.string.isRequired };

  const controls = [
    {
      text: 'Regresar',
      icon: <ArrowLeftOutlined />,
      onClick: () => onCancel(),
    },
    {
      text: 'Buscar',
      icon: <SearchOutlined />,
      onClick: () => onFinish(),
    },
  ];

  return (
    <Modal
      width="90%"
      visible={visible}
      closable={false}
      footer={null}
      maskClosable={!loading}
      keyboard={!loading}
      title={(
        <FormSubmitControls
          label="Búsqueda Recibos"
          controls={controls}
          loading={loading}
        />
      )}
      onCancel={onCancel}
      forceRender
      centered
    >
      <Spin tip="Cargando..." spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          onFinish={() => onFinish()}
          scrollToFirstError
        >
          <Row gutter={10}>
            <Title text="Datos de Recibo" />
            <Col xs={24} sm={24} md={8}>
              <NumericInput
                name="importe_total"
                label="Importe total"
                decimal
              />
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="fecha_de_creacion"
                label="Fecha de creación"
              >
                <DatePicker
                  placeholder=""
                  disabledDate={(date) => date.isAfter(new Date())}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="fecha_de_cancelacion_o_devolucion"
                label="Fecha de cancelación"
              >
                <DatePicker
                  placeholder=""
                  disabledDate={(date) => date.isAfter(new Date())}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>

            {permissionCiudadano?.view && (
              <>
                <Title text="Datos de Cuenta Única Ciudadana" />
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="cuenta_unica__nombre"
                    rules={rules.cuenta_unica__nombre}
                    label="Nombre"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="cuenta_unica__apellido_paterno"
                    rules={rules.cuenta_unica__apellido_paterno}
                    label="Apellido paterno"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="cuenta_unica__apellido_materno"
                    rules={rules.cuenta_unica__apellido_materno}
                    label="Apellido materno"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="cuenta_unica__clave_ciudadana"
                    label="Clave"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="cuenta_unica__RFC"
                    rules={rules.cuenta_unica__RFC}
                    label="Rfc"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="cuenta_unica__CURP"
                    rules={rules.cuenta_unica__CURP}
                    label="Curp"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="cuenta_unica__email"
                    rules={rules.cuenta_unica__email}
                    label="Correo electrónico"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    name="cuenta_unica__numero_de_celular"
                    label="No. celular"
                    maxLength={10}
                    required={false}
                  />

                </Col>
              </>
            )}
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

BusquedaRecibo.propTypes = {
  setVisible: PropTypes.func.isRequired,
  onFinishBusqueda: PropTypes.func.isRequired,
  visible: PropTypes.bool,
};

BusquedaRecibo.defaultProps = {
  visible: false,
};

export default BusquedaRecibo;
